import React, { useEffect, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux'
import Placeholder from 'components/Shared/Common/Placeholder';
import { getUserByIdSelector } from 'selectors/user';
import { getFileSource } from 'utils/file';
import { FULL_DATE_FORMAT_CONFIG } from 'utils/date';
import MessageProcessing from 'components/Shared/Common/Spinner';
import { Image } from 'antd';

import useWhyDidYouUpdate from 'hooks/useWhyDidYouUpdate';
import CategoryTitle from 'components/Shared/Layout/CategoryTitle';
import Icon from 'components/Shared/Common/Icon';
import FileItem from './Item';
import { Styled } from './styles';
import FeedDrawer from '../FeedDrawer';

const formatMessages = intl => ({ firstname = '', lastname = '', updatedAt = null }) => ({
    drawerTitle: intl.formatMessage({
        id: 'smallDrawer.feed.gallery.title',
        defaultMessage: 'Documents'
    }),
    placeholder: intl.formatMessage({
        id: 'smallDrawer.feed.gallery.NoContent',
        defaultMessage: 'Aucun fichier n\'a été partagé pour le moment.'
    }),
    loading: intl.formatMessage({ id: 'global.loading' }),
    userName: intl.formatMessage({
        id: 'user.username',
        defaultMessage: '{firstname} {lastname}'
    }, {
        firstname,
        lastname
    }),
    formattedDate: intl.formatDate(updatedAt, FULL_DATE_FORMAT_CONFIG)
});

const FileItemContainer = memo((props) => {
    const { _currentUser, intl, onPreview, onDelete, item: feedItem } = props;
    const { metasFile, user: _user, updatedAt, content, id: _feedItem } = feedItem || {};
    const { uri, thumbnailUri, id, name, isDisplayable, extension } = metasFile || {};
    const userState = useSelector(state => getUserByIdSelector(state, _user));
    const user = userState || {};
    const { lastname, firstname } = user || {};
    const src = metasFile && uri ? getFileSource(uri, true) : null;
    const srcThumb = metasFile && thumbnailUri ? getFileSource(thumbnailUri, false) : null;
    const isInitiator = _currentUser === user.id;

    const handleFilePreview = useCallback(() => onPreview(_feedItem), [onPreview, _feedItem]);
    const handleDelete = useCallback(() => onDelete(_feedItem, content), [onDelete, _feedItem, content]);

    useWhyDidYouUpdate('components:feed:Gallery:drawer:FileItemContainer', props);

    const i18n = formatMessages(intl)({ firstname, lastname, updatedAt });

    if (!feedItem) {
        return null;
    }

    return (
        <FileItem
            key={id}
            name={name}
            author={i18n.userName}
            date={i18n.formattedDate}
            onPreview={!isDisplayable ? handleFilePreview : undefined}
            src={src}
            thumb={srcThumb}
            isDisplayable={isDisplayable}
            extension={extension}
            onDelete={isInitiator ? handleDelete : undefined}
        />
    );

});

const GalleryDrawer = (props) => {
    const {
        getFiles,
        _feed = null,
        intl,
        onClose,
        isLoading = false,
        items = [],
        _currentUser,
        onFilePreview,
        deleteFeedItem
    } = props;
    const i18n = formatMessages(intl)({});

    useWhyDidYouUpdate('components:feed:Gallery:drawer', props);

    useEffect(() => {
        if (_feed) {
            getFiles();
        }
    }, [_feed]);

    const renderItem = (item) => (
        <FileItemContainer
            key={item.id}
            item={item}
            _currentUser={_currentUser}
            intl={intl}
            onPreview={onFilePreview}
            onDelete={deleteFeedItem}
        />
    );

    let content = null;

    if (isLoading || !items) {
        content = <MessageProcessing message={i18n.loading} align="top" />;
    } else if (items.length) {
        content = (
            <Image.PreviewGroup>
                {items.map(renderItem)}
            </Image.PreviewGroup>
        )
    } else {
        content = <Placeholder name="placeholderFiles" title={i18n.placeholder} />;
    }

    return (
        <FeedDrawer
            onClose={onClose}
            isOpen
            title={(
                <CategoryTitle
                    label={i18n.drawerTitle}
                    icon={<Icon size="xl" iconName="Paperclip" />}
                />
            )}
            content={(
                <Styled className="ui-list-files">
                    {content}
                </Styled>
            )}
        />
    );
}

GalleryDrawer.propTypes = {
    intl: PropTypes.any.isRequired,
    onClose: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.object),
    _feed: PropTypes.string,
    onFilePreview: PropTypes.func.isRequired,
    getFiles: PropTypes.func.isRequired,
    deleteFeedItem: PropTypes.func.isRequired,
    _currentUser: PropTypes.string.isRequired
};

export default injectIntl(GalleryDrawer);
